
import { useState } from 'react'
import { Icon } from '@/components/ui/Icon'
import { FEEDBACK_KIND_LABELS, type FeedbackDTO } from '@/lib/feedback'
import { formatDate } from '@/lib/format'
import { ROLE_LABELS, type WorkspaceRole } from '@/lib/roles'
import { btn, card, cx, inputSm, notice, segment, tag, tagTone } from '@/lib/ui'
import { errorMessage } from '@/lib/client-api'

type WorkspaceRow = {
  id: string
  name: string
  slug: string
  createdAt: string
  members: number
  projects: number
  devices: number
  powerPlans: number
}
type PersonRow = {
  userId: string
  email: string
  name: string | null
  createdAt: string
  workspaces: { slug: string; role: WorkspaceRole }[]
}

type Tab = 'feedback' | 'workspaces' | 'people'

export function AdminClient({
  workspaces,
  people,
  feedback: initialFeedback,
  totals,
}: {
  workspaces: WorkspaceRow[]
  people: PersonRow[]
  feedback: FeedbackDTO[]
  totals: { projects: number; placedDevices: number; powerPlans: number; workspaceDevices: number }
}) {
  const [feedback, setFeedback] = useState(initialFeedback)
  const open = feedback.filter((f) => !f.resolvedAt).length
  // Feedback leads when there is any, because it is the only thing here that
  // asks anything of you. Otherwise the numbers do.
  const [tab, setTab] = useState<Tab>(open > 0 ? 'feedback' : 'workspaces')

  return (
    <>
      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="People" value={people.length} />
        <Stat label="Workspaces" value={workspaces.length} />
        <Stat label="Projects" value={totals.projects} />
        <Stat label="Devices placed" value={totals.placedDevices} />
        <Stat label="Power plans" value={totals.powerPlans} />
        <Stat label="Feedback open" value={open} tone={open > 0 ? 'warning' : undefined} />
      </div>

      <div className="mt-6 flex flex-wrap gap-1.5">
        {(['feedback', 'workspaces', 'people'] as Tab[]).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setTab(option)}
            aria-pressed={tab === option}
            className={cx(segment.base, tab === option ? segment.on : segment.off)}
          >
            {option === 'feedback'
              ? `Feedback${open > 0 ? ` · ${open}` : ''}`
              : option === 'workspaces'
                ? `Workspaces · ${workspaces.length}`
                : `People · ${people.length}`}
          </button>
        ))}
      </div>

      <div className="mt-4">
        {tab === 'feedback' && <FeedbackList feedback={feedback} onChange={setFeedback} />}
        {tab === 'workspaces' && <WorkspaceList workspaces={workspaces} />}
        {tab === 'people' && <PeopleList people={people} />}
      </div>
    </>
  )
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'warning' }) {
  return (
    <div className={cx(card, 'px-4 py-3')}>
      <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted">{label}</p>
      <p className={cx('mt-0.5 text-xl font-semibold tabular-nums', tone === 'warning' && 'text-warning')}>
        {value}
      </p>
    </div>
  )
}

function FeedbackList({
  feedback,
  onChange,
}: {
  feedback: FeedbackDTO[]
  onChange: (next: FeedbackDTO[]) => void
}) {
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [replies, setReplies] = useState<Record<string, string>>({})

  async function patch(id: string, body: { resolved: boolean; reply?: string | null }) {
    setBusy(id)
    setError(null)
    try {
      const response = await fetch(`/api/feedback/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!response.ok) {
        const detail = (await response.json().catch(() => null)) as { error?: string } | null
        throw new Error(detail?.error ?? 'Could not update that')
      }
      const updated = (await response.json()) as FeedbackDTO
      onChange(feedback.map((f) => (f.id === updated.id ? updated : f)))
      setReplies((current) => ({ ...current, [id]: '' }))
    } catch (cause) {
      setError(errorMessage(cause, 'Could not update that'))
    } finally {
      setBusy(null)
    }
  }

  if (feedback.length === 0) {
    return (
      <div className={cx(card, 'px-5 py-10 text-center text-sm text-muted')}>
        Nothing reported yet.
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {error && <p role="alert" className={notice.error}>{error}</p>}
      {feedback.map((item) => (
        <article key={item.id} className={cx(card, 'px-5 py-4')}>
          <div className="flex flex-wrap items-center gap-2">
            <span className={cx(tag, item.resolvedAt ? tagTone.success : tagTone.warning)}>
              {item.resolvedAt ? 'done' : 'open'}
            </span>
            <span className="text-sm font-medium">
              {FEEDBACK_KIND_LABELS[item.kind] ?? item.kind}
            </span>
            <span className="text-[11px] text-muted">
              {item.submittedByEmail}
              {item.workspaceSlug && ` · ${item.workspaceSlug}`} · {formatDate(item.createdAt)}
            </span>
          </div>

          <p className="mt-2 whitespace-pre-wrap text-sm">{item.message}</p>

          {/* Context, as plain text. pagePath is a stranger-supplied string and
              is never turned into a link here. */}
          <p className="mt-2 font-mono text-[10px] text-muted">
            {[item.pagePath, item.appVersion ? `build ${item.appVersion.slice(0, 7)}` : null, item.userAgent]
              .filter(Boolean)
              .join('  ·  ')}
          </p>

          {item.resolvedAt ? (
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[var(--border)] pt-3">
              <p className="flex-1 text-[11px]">
                <span className="text-muted">Replied: </span>
                {item.reply}
                <span className="text-muted">
                  {' '}
                  — {item.resolvedBy} · {formatDate(item.resolvedAt)}
                </span>
              </p>
              <button
                type="button"
                disabled={busy === item.id}
                onClick={() => void patch(item.id, { resolved: false })}
                className={cx(btn.sm, btn.ghost)}
              >
                Reopen
              </button>
            </div>
          ) : (
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[var(--border)] pt-3">
              <input
                aria-label={`Reply to ${item.id}`}
                value={replies[item.id] ?? ''}
                onChange={(event) =>
                  setReplies((current) => ({ ...current, [item.id]: event.target.value }))
                }
                maxLength={2000}
                placeholder="What you did — they will see this"
                className={cx(inputSm, 'flex-1')}
              />
              <button
                type="button"
                disabled={busy === item.id || !(replies[item.id] ?? '').trim()}
                onClick={() =>
                  void patch(item.id, { resolved: true, reply: (replies[item.id] ?? '').trim() })
                }
                className={cx(btn.sm, btn.primary)}
              >
                <Icon name="check" className="h-3.5 w-3.5" />
                Resolve
              </button>
            </div>
          )}
        </article>
      ))}
    </div>
  )
}

function WorkspaceList({ workspaces }: { workspaces: WorkspaceRow[] }) {
  return (
    <div className={cx(card, 'overflow-hidden')}>
      {workspaces.map((workspace) => (
        <div
          key={workspace.id}
          className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b border-[var(--border)] px-5 py-3 last:border-b-0"
        >
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{workspace.name}</p>
            <p className="font-mono text-[11px] text-muted">/w/{workspace.slug}</p>
          </div>
          <p className="text-[11px] tabular-nums text-muted">
            {workspace.members} {workspace.members === 1 ? 'member' : 'members'} ·{' '}
            {workspace.projects} {workspace.projects === 1 ? 'project' : 'projects'} ·{' '}
            {workspace.devices} own devices · {workspace.powerPlans} power plans
          </p>
          <p className="text-[11px] text-muted">{formatDate(workspace.createdAt)}</p>
        </div>
      ))}
    </div>
  )
}

function PeopleList({ people }: { people: PersonRow[] }) {
  return (
    <div className={cx(card, 'overflow-hidden')}>
      {people.map((person) => (
        <div
          key={person.userId}
          className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b border-[var(--border)] px-5 py-3 last:border-b-0"
        >
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{person.name ?? person.email}</p>
            {person.name && <p className="truncate text-[11px] text-muted">{person.email}</p>}
          </div>
          <p className="text-[11px] text-muted">
            {person.workspaces.length === 0
              ? 'no workspace yet'
              : person.workspaces
                  .map((w) => `${w.slug} (${ROLE_LABELS[w.role]?.toLowerCase() ?? w.role})`)
                  .join(' · ')}
          </p>
          <p className="text-[11px] text-muted">joined {formatDate(person.createdAt)}</p>
        </div>
      ))}
    </div>
  )
}
